<script setup lang="ts">
type FooterLink = {
  label: string
  url?: string
}

type FooterLinkInput = string | { label?: string; url?: string }

const props = defineProps<{
  text: string
  icp?: string
  publicSecurityRecord?: string
  externalLinks?: FooterLinkInput[]
  showFeedLink?: boolean
  showSitemapLink?: boolean
}>()

const extractLabelFromUrl = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '')
  } catch {
    return url
  }
}

const parseFooterLinks = (raw: FooterLinkInput[]) =>
  (raw || [])
    .map<FooterLink | null>(item => {
      if (typeof item === 'string') {
        const text = item.trim()
        if (!text) {
          return null
        }
        if (/^https?:\/\//i.test(text)) {
          return { label: extractLabelFromUrl(text), url: text }
        }
        return { label: text }
      }
      const url = String(item?.url || '').trim()
      const label = String(item?.label || '').trim() || (url ? extractLabelFromUrl(url) : '')
      if (!label) {
        return null
      }
      return {
        label,
        url: url || undefined
      }
    })
    .filter((item): item is FooterLink => !!item)

const footerLinks = computed(() => parseFooterLinks(props.externalLinks || []))
const footerRowCount = computed(() => Math.max(
  1 + Number(footerLinks.value.length > 0) + Number(Boolean(props.showFeedLink || props.showSitemapLink)),
  1 + Number(Boolean(props.icp || props.publicSecurityRecord))
))
const publicSecurityCode = computed(() => {
  const source = String(props.publicSecurityRecord || '')
  const digits = source.match(/\d+/g)
  return digits ? digits.join('') : ''
})
const publicSecurityLink = computed(() => {
  const code = publicSecurityCode.value
  if (!code) {
    return ''
  }
  return `https://www.beian.gov.cn/portal/registerSystemInfo?recordcode=${encodeURIComponent(code)}`
})
</script>

<template>
  <footer class="footer-wrap">
    <div class="container footer-inner" :style="{ '--footer-row-count': footerRowCount }">
      <div class="footer-left">
        <p class="footer-text">{{ text }}</p>
        <template v-if="footerLinks.length">
          <p class="footer-links-line">
            <template v-for="(item, idx) in footerLinks" :key="`${item.label}:${idx}`">
              <span v-if="idx > 0" class="sep">|</span>
              <a v-if="item.url" class="footer-link has-url" :href="item.url" target="_blank" rel="noopener noreferrer">
                {{ item.label }}
              </a>
              <span v-else class="footer-link">{{ item.label }}</span>
            </template>
          </p>
        </template>
        <div v-if="showFeedLink || showSitemapLink" class="seo-links" aria-label="Feed and Sitemap">
          <a v-if="showFeedLink" href="/feed.xml" class="seo-link" target="_blank" rel="noopener noreferrer" title="Feed">
            <i class="fa-solid fa-rss" aria-hidden="true" />
          </a>
          <a v-if="showSitemapLink" href="/sitemap.xml" class="seo-link" target="_blank" rel="noopener noreferrer" title="Sitemap">
            <i class="fa-solid fa-sitemap" aria-hidden="true" />
          </a>
        </div>
      </div>

      <div class="footer-right">
        <p v-if="icp || publicSecurityRecord" class="icp-line">
          <a v-if="icp" href="http://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">{{ icp }}</a>
          <span v-if="icp && publicSecurityRecord" class="icp-sep">|</span>
          <a v-if="publicSecurityRecord" :href="publicSecurityLink" target="_blank" rel="noopener noreferrer">{{ publicSecurityRecord }}</a>
        </p>
        <a href="https://www.yuelaigroup.com" target="_blank" rel="noopener noreferrer">Powered by Yuelai Engine</a>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer-wrap {
  padding: 20px 0 30px;
}

.footer-inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-rows: repeat(var(--footer-row-count), auto);
  gap: 10px 16px;
  padding: 16px;
  border-top: 1px solid var(--line);
}

.footer-left,
.footer-right {
  display: grid;
  grid-row: 1 / -1;
  grid-template-rows: subgrid;
  align-items: start;
  min-width: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-soft);
  word-break: break-word;
}

.footer-left {
  justify-self: start;
  text-align: left;
}

.footer-text,
.footer-links-line,
.icp-line {
  margin: 0;
  line-height: inherit;
}

.icp-sep {
  margin: 0 6px;
  color: color-mix(in srgb, var(--text-soft) 72%, transparent);
}

.icp-line a,
.footer-right a,
.footer-link.has-url,
.seo-link {
  color: var(--text-soft);
  text-decoration: none;
  transition:
    color 0.18s ease,
    opacity 0.18s ease;
}

.footer-link.has-url:hover,
.icp-line a:hover,
.footer-right a:hover,
.seo-link:hover {
  color: var(--brand);
}

.footer-link.has-url:hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.sep {
  margin: 0 6px;
  color: color-mix(in srgb, var(--text-soft) 72%, transparent);
}

.seo-links {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.seo-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
}

.seo-link i {
  font-size: 15px;
}

.footer-right {
  justify-self: end;
  justify-items: end;
  text-align: right;
  white-space: nowrap;
}

@media (max-width: 760px) {
  .seo-links {
    display: none;
  }

  .footer-inner {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }

  .footer-left,
  .footer-right {
    grid-row: auto;
    grid-template-rows: none;
    row-gap: inherit;
    justify-self: stretch;
    justify-items: start;
    text-align: left;
    white-space: normal;
  }
}
</style>
